import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { importAPI } from '../services/api';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Helper to handle zoom events
function MapEvents({ onZoomEnd }) {
    useMapEvents({
        zoomend: (e) => {
            onZoomEnd(e.target.getZoom());
        }
    });
    return null;
}

// Helper to fit bounds
function MapBounds({ data }) {
    const map = useMap();
    useEffect(() => {
        if (data) {
            try {
                const layer = L.geoJSON(data);
                const bounds = layer.getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds);
                }
            } catch (e) {
                console.error("Invalid GeoJSON for bounds:", e);
            }
        }
    }, [data, map]);
    return null;
}

const MapPreviewPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const cemetery = searchParams.get('cemetery');
    const fileId = searchParams.get('file_id');
    const fileName = searchParams.get('filename') || 'Map Preview';

    const [geoJsonData, setGeoJsonData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(13); // Default

    useEffect(() => {
        if (!cemetery || !fileId) {
            setError("Missing parameters");
            setLoading(false);
            return;
        }
        loadData();
    }, [cemetery, fileId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await importAPI.getFileContent(cemetery, fileId);
            const text = await response.data.text();
            const json = JSON.parse(text);
            setGeoJsonData(json);
        } catch (err) {
            console.error("Failed to load map data:", err);
            setError("Failed to load map data. ensure backend is running.");
        } finally {
            setLoading(false);
        }
    };

    // Memoized optimization logic
    const shouldRenderPlots = useMemo(() => {
        if (!geoJsonData) return false;

        // Strategy:
        // 1. If small dataset (< 1000 features), always render
        // 2. If large dataset, only render if zoom >= 18

        let featureCount = 0;
        if (geoJsonData.type === 'FeatureCollection') {
            featureCount = geoJsonData.features.length;
        }

        if (featureCount < 1000) return true;
        return zoomLevel >= 18;
    }, [geoJsonData, zoomLevel]);

    // Handle large feature logic
    const featureCount = geoJsonData?.features?.length || 0;
    const isLargeFile = featureCount >= 1000;

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 20px', background: '#f8f9fa', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    <h2 style={{ margin: 0 }}>{fileName}</h2>
                    <span style={{ fontSize: '0.9rem', color: '#666' }}>
                        {featureCount} features • Zoom: {zoomLevel}
                        {isLargeFile && !shouldRenderPlots && (
                            <span style={{ color: 'orange', marginLeft: '10px' }}>
                                (Zoom in to view plots)
                            </span>
                        )}
                    </span>
                </div>
                <button
                    onClick={() => navigate(`/import?cemetery=${cemetery}`)}
                    className="btn btn-secondary"
                >
                    Back to Import
                </button>
            </div>

            <div style={{ flex: 1, position: 'relative' }}>
                {loading && (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000, background: 'rgba(255,255,255,0.8)', padding: '20px' }}>
                        Loading Map Data...
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger" style={{ margin: '20px' }}>
                        {error}
                    </div>
                )}

                {!loading && !error && geoJsonData && (
                    <MapContainer
                        center={[-27.47, 153.02]}
                        zoom={13}
                        style={{ height: '100%', width: '100%' }}
                        preferCanvas={true}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        <MapEvents onZoomEnd={setZoomLevel} />
                        <MapBounds data={geoJsonData} />

                        {shouldRenderPlots && (
                            <GeoJSON
                                data={geoJsonData}
                                style={{
                                    color: '#3388ff',
                                    weight: 1,
                                    fillOpacity: 0.2
                                }}
                            />
                        )}
                    </MapContainer>
                )}
            </div>
        </div>
    );
};

export default MapPreviewPage;
